import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { NavLink } from "react-router-dom";
import { ArrowRight, Star, ExternalLink } from "lucide-react";
import { useLangStore } from "@/lib/langStore";
import { translations } from "@/lib/i18n";
import { ROUTE_PATHS, PROJECTS, TESTIMONIALS } from "@/lib/index";
import { ProjectCard } from "@/components/ProjectCard";
import Layout from "@/components/Layout";
import { useEffect, useRef, useState } from "react";

// Animated counter
function Counter({ end, suffix = "" }: { end: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const duration = 2000;
          const step = end / (duration / 16);
          const timer = setInterval(() => {
            start += step;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
}

import Magnetic from "@/components/Magnetic";

export default function HomePage() {
  const { lang } = useLangStore();
  const t = translations[lang];
  const featuredProjects = PROJECTS.filter((p) => p.featured);

  // Mouse parallax for hero
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 100, damping: 30 });
  const springY = useSpring(mouseY, { stiffness: 100, damping: 30 });

  const handleMouseMove = (e: React.MouseEvent) => {
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    mouseX.set((clientX - innerWidth / 2) / 25);
    mouseY.set((clientY - innerHeight / 2) / 25);
  };

  const marqueeText = lang === "en" 
    ? ["LOGO DESIGN", "BRAND IDENTITY", "TYPOGRAPHY", "VISUAL STRATEGY", "MOTION GRAPHICS", "CREATIVE DIRECTION"]
    : ["تصميم شعارات", "هوية بصرية", "تايبوجرافي", "استراتيجية بصرية", "موشن جرافيك", "إخراج إبداعي"];

  return (
    <Layout>
      {/* Hero */}
      <section 
        onMouseMove={handleMouseMove}
        className="relative min-h-screen flex items-center overflow-hidden pt-20"
      >
        {/* Background elements with Parallax */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            style={{ x: springX, y: springY }}
            animate={{
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-primary/10 blur-[120px]"
          />
          <motion.div
            style={{ x: useTransform(springX, (v) => -v), y: useTransform(springY, (v) => -v) }}
            animate={{
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full bg-accent/10 blur-[120px]"
          />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, oklch(0.60 0.24 255) 1px, transparent 0)",
              backgroundSize: "48px 48px",
            }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex flex-col items-center">
            {/* Tagline badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-medium mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              {t.home.heroTagline}
            </motion.div>

            {/* Main headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="text-5xl sm:text-6xl lg:text-8xl font-extrabold tracking-tighter text-foreground mb-6 leading-[1.1]"
            >
              {t.home.heroTitle.split(" ").map((word, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  className="inline-block mr-[0.2em]"
                >
                  {word}
                </motion.span>
              ))}{" "}
              <motion.span
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.8, ease: "easeOut" }}
                className="text-transparent bg-clip-text inline-block"
                style={{
                  backgroundImage: "linear-gradient(135deg, oklch(0.60 0.24 255), oklch(0.72 0.20 40))",
                }}
              >
                {t.home.heroTitleHighlight}
              </motion.span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="max-w-2xl text-lg sm:text-xl text-muted-foreground leading-relaxed mb-10"
            >
              {t.home.heroSubtitle}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Magnetic>
                <NavLink
                  to={ROUTE_PATHS.PORTFOLIO}
                  className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-primary text-primary-foreground font-semibold text-base hover:bg-primary/90 active:scale-95 transition-all duration-200 shadow-xl shadow-primary/25"
                >
                  {t.home.ctaWork}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </NavLink>
              </Magnetic>
              <Magnetic>
                <NavLink
                  to={ROUTE_PATHS.CONTACT}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-border bg-card/50 text-foreground font-semibold text-base hover:bg-card hover:border-primary/50 active:scale-95 transition-all duration-200"
                >
                  {t.home.ctaContact}
                </NavLink>
              </Magnetic>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <div className="w-5 h-9 rounded-full border-2 border-border flex justify-center pt-1.5">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
              className="w-1 h-2 rounded-full bg-primary/60"
            />
          </div>
        </motion.div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-border bg-card/30 relative overflow-hidden">
        {/* Infinite Marquee */}
        <div className="absolute top-0 left-0 right-0 h-full flex items-center opacity-[0.03] pointer-events-none select-none">
          <motion.div 
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            className="flex whitespace-nowrap gap-20 text-[12vw] font-black"
          >
            {[...marqueeText, ...marqueeText].map((text, i) => (
              <span key={i}>{text}</span>
            ))}
          </motion.div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: 3, suffix: "+", label: t.home.statsProjects },
              { value: 2, suffix: "+", label: t.home.statsClients },
              { value: 2, suffix: "", label: t.home.statsYears },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl lg:text-5xl font-extrabold text-foreground mb-1">
                  <Counter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-muted-foreground font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-12">
            <div>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-primary font-semibold text-sm uppercase tracking-wider mb-2"
              >
                ✦ {lang === "en" ? "Selected Work" : "أعمال مختارة"}
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl lg:text-4xl font-extrabold text-foreground"
              >
                {t.home.featuredWork}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-muted-foreground mt-2"
              >
                {t.home.featuredSubtitle}
              </motion.p>
            </div>
            <NavLink
              to={ROUTE_PATHS.PORTFOLIO}
              className="group inline-flex items-center gap-2 text-primary font-semibold text-sm hover:gap-3 transition-all duration-200 shrink-0"
            >
              {t.home.viewAll}
              <ArrowRight size={16} />
            </NavLink>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProjects.map((project, i) => (
              <ProjectCard key={project.id} project={project} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 lg:py-28 bg-card/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-primary font-semibold text-sm uppercase tracking-wider mb-2"
            >
              ✦ {lang === "en" ? "How I Work" : "كيف أعمل"}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl lg:text-4xl font-extrabold text-foreground mb-3"
            >
              {t.home.processTitle}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-muted-foreground"
            >
              {t.home.processSubtitle}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { num: "01", title: t.home.process1Title, desc: t.home.process1Desc, icon: "🔍" },
              { num: "02", title: t.home.process2Title, desc: t.home.process2Desc, icon: "💡" },
              { num: "03", title: t.home.process3Title, desc: t.home.process3Desc, icon: "✏️" },
              { num: "04", title: t.home.process4Title, desc: t.home.process4Desc, icon: "📦" },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative p-6 rounded-2xl border border-border bg-card group hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300"
              >
                <div className="text-3xl mb-4">{step.icon}</div>
                <div className="text-xs font-mono text-primary/70 font-bold mb-1">{step.num}</div>
                <h3 className="font-bold text-foreground text-lg mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-primary font-semibold text-sm uppercase tracking-wider mb-2"
            >
              ✦ {lang === "en" ? "Testimonials" : "شهادات العملاء"}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl lg:text-4xl font-extrabold text-foreground"
            >
              {t.home.testimonialsTitle}
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl border border-border bg-card"
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: item.rating }).map((_, j) => (
                    <Star key={j} size={14} className="fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed mb-5 italic">
                  "{lang === "ar" ? item.textAr : item.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
                    {item.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-foreground">
                      {lang === "ar" ? item.nameAr : item.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {lang === "ar" ? item.roleAr : item.role} · {item.company}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 mx-4 mb-4 rounded-3xl overflow-hidden relative"
        style={{ background: "#00807A" }}>
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl lg:text-4xl font-extrabold text-white mb-4"
          >
            {lang === "en" ? "Ready to build your brand?" : "هل أنت مستعد لبناء علامتك التجارية؟"}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-white/80 mb-8 text-lg"
          >
            {lang === "en"
              ? "Let's craft a logo that speaks volumes about who you are."
              : "لنصمم شعاراً يتحدث بصوت عالٍ عن هويتك."}
          </motion.p>
          <NavLink
            to={ROUTE_PATHS.CONTACT}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white text-primary font-bold hover:bg-white/90 active:scale-95 transition-all duration-200 shadow-xl"
          >
            {t.home.ctaContact} <ArrowRight size={18} />
          </NavLink>
        </div>
      </section>
    </Layout>
  );
}
